import React from 'react';
import {Listbox} from '@headlessui/react';
import UIStdOptionList from './UIStdOptionList';
import UIStdSelectButton from './UIStdSelectButton';
import {IOptGroup, IOption} from './UIOption';

/**
 * @param preselected {boolean} If true, the first option from the list of options is selected
 * @param emptyLabel {string} Label to show when no option is selected
 */
type ListBoxProps = {
  options: (IOption<boolean> | IOptGroup<boolean>)[]
  onChange: (value: string) => void
  className?: string
  disabled?: boolean
  value?: string
  emptyLabel?: string
  as?: 'div' | 'span'
}

function isOptGroup(value: IOption<boolean> | IOptGroup<boolean>): value is IOptGroup<boolean> {
  return (value as IOptGroup<boolean>).options !== undefined;
}

export default function UIListBox({
  className,
  disabled = false,
  value,
  onChange,
  options,
  emptyLabel = 'Select an item',
  as = 'div',
}: ListBoxProps) {
  const rawOptions: IOption<boolean>[] = options
    .map((x) => (isOptGroup(x) ? x.options : [x]))
    .flat(1);

  const toOptions = (group: IOptGroup<boolean>) => {
    const parent: IOption<boolean> = {
      id: group.id, label: group.label, value: group.id, selected: false, parent: true,
    };
    const children = group.options.map((x) => ({...x, parent: false}));
    return [parent].concat(children);
  };

  const getSelectedLabel = (mayBeValue: string | undefined) => {
    if (!mayBeValue) {
      return <span className="text-gray">{emptyLabel}</span>;
    }
    const selectedOption = rawOptions.find((x) => x.value === mayBeValue);
    return selectedOption?.label ?? <span className="text-gray">{emptyLabel}</span>;
  };

  const items: IOption<boolean>[] = options.map((x) => (isOptGroup(x) ? toOptions(x) : [x])).flat(1);

  const selectedValue =  value || items.find((x) => x.selected)?.value

  return (
    <Listbox value={selectedValue} onChange={onChange} as={as} className={className} disabled={disabled}>
      {({open}) => (
        <div className="relative">
          <UIStdSelectButton value={getSelectedLabel(selectedValue)} disabled={disabled}/>
          <UIStdOptionList options={items} open={open}/>
        </div>
      )}
    </Listbox>
  );
}
